import type { QueryClient } from '@tanstack/react-query';
import { notifyManager, useQueryClient } from '@tanstack/react-query';
import { toast } from '@tempo-io/sui-sonner';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ItemsApi } from '@/common/api/items';
import type { ItemTreeSet, ItemTreeSetEntry } from '@/common/api/types/items';
import type { FetchedTreeSet, TreeSetPredicate } from '@/common/queries/itemsTreeSetConsistency';
import {
  cancelPredicateReconciliation,
  invalidateItemsTreeSet,
  treeSetReordered,
} from '@/common/queries/itemsTreeSetConsistency';
import { reconcileAndSettle } from '@/common/queries/itemsTreeSetSettleState';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { lazyMapFor } from '@/common/utils/lazyMapFor';
import { isPriorityOrderedTreeSetKey } from '@/features/items/queryKeys/itemsTreeSetKey';
import { createReassertCycleGuard, type ReassertCycleGuard } from './reassertCycleGuard';
import { claimRows } from './rowReorderClaims';

export type RelativePriority = { id: number; type: 'before' | 'after' };

const subtreeEndIndex = (
  entries: readonly ItemTreeSetEntry[],
  byId: ReadonlyMap<number, ItemTreeSetEntry>,
  startIdx: number,
  rootId: number,
): number => {
  let end = startIdx + 1;
  while (end < entries.length) {
    let cur: ItemTreeSetEntry | undefined = entries[end];
    let isDescendant = false;
    while (cur?.parentId != null) {
      if (cur.parentId === rootId) {
        isDescendant = true;
        break;
      }
      cur = byId.get(cur.parentId);
    }
    if (!isDescendant) break;
    end++;
  }
  return end;
};

export const reorderTreeSetData = (
  entries: readonly ItemTreeSetEntry[],
  movedId: number,
  targetSiblingId: number,
  position: 'above' | 'below',
): readonly ItemTreeSetEntry[] => {
  const movedIdx = entries.findIndex((e) => e.id === movedId);
  if (movedIdx === -1) return entries;

  const byId = new Map(entries.map((e) => [e.id, e]));
  const movedParentId = entries[movedIdx]?.parentId ?? null;
  const subtreeEnd = subtreeEndIndex(entries, byId, movedIdx, movedId);
  const subtree = entries.slice(movedIdx, subtreeEnd);
  const withoutSubtree = [...entries.slice(0, movedIdx), ...entries.slice(subtreeEnd)];

  const targetIdx = withoutSubtree.findIndex((e) => e.id === targetSiblingId);
  if (targetIdx === -1) return entries;
  if ((withoutSubtree[targetIdx]?.parentId ?? null) !== movedParentId) return entries;

  const insertAt =
    position === 'above'
      ? targetIdx
      : subtreeEndIndex(withoutSubtree, byId, targetIdx, targetSiblingId);

  return [...withoutSubtree.slice(0, insertAt), ...subtree, ...withoutSubtree.slice(insertAt)];
};

const rollbacksByClient = new WeakMap<QueryClient, Map<number, () => void>>();
const rollbacksFor = (queryClient: QueryClient) => lazyMapFor(rollbacksByClient, queryClient);

export const forgetRollbackBaselines = (queryClient: QueryClient, ids: readonly number[]) => {
  const rollbacks = rollbacksFor(queryClient);
  for (const id of ids) rollbacks.delete(id);
};

export const reorderInCache = (
  queryClient: QueryClient,
  movedId: number,
  targetSiblingId: number,
  position: 'above' | 'below',
) => {
  queryClient.setQueriesData<ItemTreeSet>(
    {
      queryKey: commonQueryKeys.itemsTreeSet.all(),
      predicate: isPriorityOrderedTreeSetKey,
    },
    (cached) => {
      if (!cached) return cached;
      const data = reorderTreeSetData(cached.data, movedId, targetSiblingId, position);
      if (data === cached.data) return cached;
      return { ...cached, data: [...data] };
    },
  );
};

const reassertCycleGuardsByClient = new WeakMap<QueryClient, ReassertCycleGuard>();
export const reassertCycleGuardFor = (queryClient: QueryClient): ReassertCycleGuard => {
  let guard = reassertCycleGuardsByClient.get(queryClient);
  if (!guard) {
    guard = createReassertCycleGuard();
    reassertCycleGuardsByClient.set(queryClient, guard);
  }
  return guard;
};

const reassertFromNotification = (
  queryClient: QueryClient,
  movedId: number,
  targetSiblingId: number,
  position: 'above' | 'below',
) => {
  reassertCycleGuardFor(queryClient).attempt(movedId, targetSiblingId, position, () =>
    reorderInCache(queryClient, movedId, targetSiblingId, position),
  );
};

const reorderSupersedeKey = (movedId: number) => `reorder:${movedId}`;

export const evaluateReordered =
  (isReflected: TreeSetPredicate, movedId: number, notBefore: number) =>
  (fetched: readonly FetchedTreeSet[]): boolean =>
    fetched
      .filter(
        (f) => isPriorityOrderedTreeSetKey({ queryKey: f.queryKey }) && f.startedAt > notBefore,
      )
      .filter((f) => f.data.data.some((entry) => entry.id === movedId))
      .some((f) => isReflected(f.data.data));

const isItemTreeSet = (data: unknown): data is ItemTreeSet =>
  typeof data === 'object' && data !== null && 'data' in data && Array.isArray(data.data);

const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError';

export const useItemRelativePriorityUpdate = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return React.useCallback(
    async (movedId: number, relativePriority: RelativePriority, rollback?: () => void) => {
      const claim = claimRows(queryClient, [movedId]);
      reassertCycleGuardFor(queryClient).clear(movedId);

      const abortController = new AbortController();
      claim.onSupersede(() => abortController.abort());
      const supersedeKey = reorderSupersedeKey(movedId);
      claim.onSupersede(() => cancelPredicateReconciliation(queryClient, supersedeKey));

      const rollbacks = rollbacksFor(queryClient);
      if (rollback && !rollbacks.has(movedId)) rollbacks.set(movedId, rollback);

      const anchorId = relativePriority.id;
      const cachePosition = relativePriority.type === 'after' ? 'below' : 'above';
      const reconcileHandle = requestAnimationFrame(() => {
        if (claim.isCurrent()) reorderInCache(queryClient, movedId, anchorId, cachePosition);
      });

      const isReflected = treeSetReordered(movedId, anchorId, relativePriority.type);
      const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
        if (!claim.isCurrent()) return;
        if (event.type !== 'updated' || event.action.type !== 'success') return;
        if (!isPriorityOrderedTreeSetKey({ queryKey: event.query.queryKey })) return;
        const data = event.query.state.data;
        if (!isItemTreeSet(data) || isReflected(data.data)) return;
        if (reorderTreeSetData(data.data, movedId, anchorId, cachePosition) === data.data) return;
        reassertFromNotification(queryClient, movedId, anchorId, cachePosition);
      });
      claim.onSupersede(unsubscribe);

      let confirmedAt: number;
      try {
        await ItemsApi.updateItem({
          itemId: movedId,
          data: { relativePriority },
          signal: abortController.signal,
        });
        confirmedAt = performance.now();
      } catch (error) {
        cancelAnimationFrame(reconcileHandle);
        unsubscribe();
        if (!isAbortError(error)) {
          toast.error(
            t(($) => $.common.errors.global.title),
            {
              description: t(($) => $.common.errors.genericWithSupport),
            },
          );
        }
        if (!claim.isCurrent()) return;

        claim.release();
        reassertCycleGuardFor(queryClient).clear(movedId);
        const firstRollback = rollbacks.get(movedId);
        rollbacks.delete(movedId);
        firstRollback?.();
        return;
      }

      void invalidateItemsTreeSet(queryClient);

      if (!claim.isCurrent()) return;

      void reconcileAndSettle(queryClient, [movedId], {
        isReflected,
        evaluateReflected: evaluateReordered(isReflected, movedId, confirmedAt),
        supersedeKey,
      })
        .catch(() => {})
        .finally(() => {
          if (claim.isCurrent()) rollbacks.delete(movedId);
          notifyManager.schedule(() => {
            unsubscribe();
            claim.release();
          });
        });
    },
    [queryClient, t],
  );
};
